import React from 'react';
import { Stack, Typography } from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { LoanExternalLead } from '@tassomutuo-backoffice/types';
import ExternalLeadCardShell from './ExternalLeadCardShell';
import { formatNumberWithThousandsSeparator } from '../../../../utils/formatMoney';

interface IProps {
  lead: LoanExternalLead;
  onOpenDetails: (id: string) => void;
}

const buildFullName = (lead: LoanExternalLead) =>
  [lead.name, lead.lastname].filter(Boolean).join(' ').trim();

const buildSource = (lead: LoanExternalLead) => {
  const parts: string[] = [];
  if (lead.from) parts.push(`Sorgente: ${lead.from}`);
  if (lead.agenzia_euroansa_da_cui_arriva_la_lead) {
    parts.push(`Agenzia: ${lead.agenzia_euroansa_da_cui_arriva_la_lead}`);
  }
  if (lead.consulente_richiesto_pagina) {
    parts.push(`Consulente: ${lead.consulente_richiesto_pagina}`);
  }
  if (lead.segnalatore) parts.push(`Segnalatore: ${lead.segnalatore}`);
  if (lead.request?.comune) {
    const cap = lead.request.cap ? `${lead.request.cap} ` : '';
    const prov = lead.request.provincia ? ` (${lead.request.provincia})` : '';
    parts.push(`${cap}${lead.request.comune}${prov}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
};

const buildProductInfo = (lead: LoanExternalLead) => {
  const req = lead.request;
  if (!req) return null;

  const hasAmount = req.loanAmount !== undefined && req.loanAmount !== null;
  const hasPurpose = !!req.loanPurpose;
  const hasContract = !!req.employmentContract;

  if (!hasAmount && !hasPurpose && !hasContract) return null;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        {hasAmount && (
          <>
            <EuroIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={500}>
              {formatNumberWithThousandsSeparator(String(req.loanAmount))}
            </Typography>
          </>
        )}
        {hasPurpose && (
          <Typography variant="body2" color="text.secondary">
            {hasAmount ? ' · ' : ''}
            {req.loanPurpose}
          </Typography>
        )}
      </Stack>
      {hasContract && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <WorkOutlineIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="text.secondary">
            {req.employmentContract}
            {req.yearsCurrentCompany ? ` · ${req.yearsCurrentCompany}` : ''}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const ExternalLeadCardLoan = ({ lead, onOpenDetails }: IProps) => {
  return (
    <ExternalLeadCardShell
      status={lead.status}
      quality={lead.quality}
      createdAt={lead.createdAt}
      fullName={buildFullName(lead)}
      email={lead.email}
      prefix={lead.prefix}
      phone={lead.phone}
      source={buildSource(lead)}
      productInfo={buildProductInfo(lead)}
      onOpenDetails={() => onOpenDetails(lead.id)}
    />
  );
};

export default ExternalLeadCardLoan;
